/**
 * Widget "Performance CEE" pour le Dashboard.
 * Affiche 3 KPIs :
 *   - Total kWh cumac engagé (dossiers non refusés)
 *   - Prime cumulée signée (€) sur dossiers dépassant la préqualification
 *   - Taux de conversion simulation → dossier
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Zap, Euro, TrendingUp, ChevronRight, Loader2 } from 'lucide-react';

const fmtEUR = (n) =>
  n == null ? '—'
  : new Intl.NumberFormat('fr-FR', {
      style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
    }).format(n);

const fmtKwh = (n) =>
  n == null ? '—'
  : `${new Intl.NumberFormat('fr-FR').format(Math.round(n))} kWh cumac`;

const fmtPct = (n) => (n == null ? '—' : `${n}%`);

export default function CeeStatsWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/cee/stats');
        setStats(r.data);
      } catch {
        // Silent : le widget est optionnel côté dashboard
        setStats(null);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5"
      data-testid="dashboard-cee-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
            <Zap className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">Performance CEE</h3>
            <p className="text-[11px] text-slate-500">Certificats d'économies d'énergie</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/cee/dossiers')}
          className="text-xs text-[#1C49A6] hover:underline inline-flex items-center gap-1"
          data-testid="dashboard-cee-widget-open"
        >
          Ouvrir <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : !stats ? (
        <p className="text-sm text-slate-400 text-center py-4">
          Module CEE indisponible.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <KpiCard
            icon={Zap}
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
            label="kWh cumac engagé"
            value={fmtKwh(stats.kwh_cumac_engaged)}
            hint={`${stats.dossiers_total || 0} dossier(s) au total`}
            testid="dashboard-cee-kwh"
          />
          <KpiCard
            icon={Euro}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50"
            label="Prime signée cumulée"
            value={fmtEUR(stats.prime_signed_eur)}
            hint={`${stats.dossiers_signed || 0} dossier(s) signé(s)`}
            testid="dashboard-cee-prime"
          />
          <KpiCard
            icon={TrendingUp}
            iconColor="text-orange-600"
            iconBg="bg-orange-50"
            label="Conversion simu → dossier"
            value={fmtPct(stats.conversion_rate)}
            hint={`${stats.simulations_converted || 0} / ${stats.simulations_completed_or_converted || 0} simu(s)`}
            testid="dashboard-cee-conv"
          />
        </div>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, iconColor, iconBg, label, value, hint, testid }) {
  return (
    <div
      className="border border-slate-100 rounded-lg p-3 hover:border-slate-200 transition"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-6 h-6 rounded ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
        </div>
        <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          {label}
        </div>
      </div>
      <div className="text-xl font-bold text-slate-900 font-mono truncate">{value}</div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
